/**
 * ComparisonLegendView - Legend dots for player comparison charts
 *
 * Renders the legend indicators shared between spider graph and distribution
 * panels: a cyan dot for the current player, an optional orange dot for a
 * compared player, and an optional dashed-gray dot for the average line.
 * Compare item names are truncated with max-w-28, numberOfLines={1},
 * and isTruncated (adds web:truncate CSS class) to prevent long list names
 * from overflowing the legend area on both native and web platforms.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import React from "react";

import LegendDot from "@/features/player-detail/components/molecules/LegendDot";

import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/**
 * Props for ComparisonLegendView component
 */
interface ComparisonLegendViewProps {
  /** Player name shown next to the cyan dot */
  readonly playerName: string;
  /** Theme text class for legend labels */
  readonly textClass: string;
  /** Whether comparing to a specific player (shows orange dot) */
  readonly isPlayerComparison: boolean;
  /** Whether to show comparison distribution average legend */
  readonly showComparisonDistribution: boolean;
  /** Whether comparing to a position (shows "Position Avg" label) */
  readonly isPositionComparison: boolean;
  /** Name of the selected compare item for the average legend label */
  readonly selectedCompareItemName: string | undefined;
}

/**
 * ComparisonLegendView displays legend dots for comparison charts
 * @param props - Component props
 * @param props.playerName - Player name shown next to the cyan dot
 * @param props.textClass - Theme text class for legend labels
 * @param props.isPlayerComparison - Whether comparing to a specific player
 * @param props.showComparisonDistribution - Whether to show comparison distribution average
 * @param props.isPositionComparison - Whether comparing to a position
 * @param props.selectedCompareItemName - Name of the selected compare item
 * @returns VStack containing legend dot rows
 */
const ComparisonLegendView = ({
  playerName,
  textClass,
  isPlayerComparison,
  showComparisonDistribution,
  isPositionComparison,
  selectedCompareItemName,
}: ComparisonLegendViewProps) => (
  <Stack className="shrink items-end gap-1">
    <Stack direction="horizontal" className="items-center gap-1">
      <LegendDot color="#22d3ee" />
      <Text
        variant="unstyled"
        className={`max-w-28 text-caption ${textClass}`}
        isTruncated
        numberOfLines={1}
      >
        {playerName}
      </Text>
    </Stack>
    {isPlayerComparison && (
      <Stack direction="horizontal" className="items-center gap-1">
        <LegendDot color="#f97316" />
        <Text
          variant="unstyled"
          className={`max-w-28 text-caption ${textClass}`}
          isTruncated
          numberOfLines={1}
        >
          {selectedCompareItemName ?? "Player"}
        </Text>
      </Stack>
    )}
    {/* Show avg legend for: position, group comparisons, OR player comparison (shows position avg as context) */}
    {(showComparisonDistribution ||
      isPositionComparison ||
      isPlayerComparison) && (
      <Stack direction="horizontal" className="items-center gap-1">
        <LegendDot color="#6b7280" isDashed />
        <Text
          variant="unstyled"
          className={`max-w-28 text-caption ${textClass}`}
          isTruncated
          numberOfLines={1}
        >
          {showComparisonDistribution
            ? `${selectedCompareItemName ?? "Comparison"} Avg`
            : "Position Avg"}
        </Text>
      </Stack>
    )}
  </Stack>
);

ComparisonLegendView.displayName = "ComparisonLegendView";

export default React.memo(ComparisonLegendView);
